import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import uniq from 'lodash/uniq'
import moment from 'moment/moment'
import { IMessage } from '@stomp/stompjs'

import { FormFiltersType, useDefaultValues } from '@pages/BarDiagramPage/hooks/useDefaultValues'
import useHandlers from '@pages/BarDiagramPage/hooks/useHandlers'
import useInputs from '@pages/BarDiagramPage/hooks/useInputs'
import { getObjectCodeUrl } from '@pages/ConfiguredEntity/helpers/getObjectCodeUrl'

import { useExecuteCommandMutation } from '@redux/api'
import { useFetchBarChartByIdMutation } from '@redux/api/barChart.api'

import { useSubscription } from '@hooks'
import { getCommandRequestData } from '@helpers'
import { INCREMENT_TYPE } from '@constants'

import {
  BarChartResponseType,
  ColumnFieldRequestType,
  DIAGRAM_FIELD_BIND_TYPE,
  SelectedBar,
} from '../../../types/api/barChart.api'

const useBarDiagram = () => {
  const { id } = useParams()

  const defaultValues = useDefaultValues()
  const methods = useForm<FormFiltersType>({ defaultValues })

  const { watch, setValue } = methods

  const watchFromDate = watch('fromDay')
  const watchQual = watch('qual')
  const watchDepartment = watch('department')
  const watchPosition = watch('position')
  const watchShowFormatDate = watch('showFormatDate')
  const watchGroup = watch('group')

  const [diagramConfig, setDiagramConfig] = useState<BarChartResponseType>()
  const [diagramDataSet, setDiagramDataSet] = useState([])
  const [selectedBar, setSelectedBar] = useState<SelectedBar>()

  const [fetchBarChartById] = useFetchBarChartByIdMutation()
  const [executeCommand] = useExecuteCommandMutation()

  const [isLoading, setIsLoading] = useState(true)

  const columns = useMemo(
    () =>
      diagramConfig?.columns.map(column => ({
        id: column.id,
        type: 'bar',
        dataKey: String(column.id),
        label: column.title,
        color: column.color,
        valueFormatter: undefined,
      })) || [],
    [diagramConfig]
  )

  useEffect(() => {
    if (watchFromDate && diagramConfig) {
      setIsLoading(true)

      configBarChartDataSet()
    }
  }, [watchFromDate, diagramConfig, watchDepartment, watchQual, watchPosition, watchGroup])

  const configBarChartDataSet = useCallback(() => {
    if (diagramConfig) {
      // Получаем данные по колонкам выполняя назначенные для них команды
      const dataPromises: Promise<{
        seriesId: number | undefined
        columnId: number
        data: any
      }>[] = diagramConfig.columns.map(column => {
        if (column.commandForEverySeries) {
          // Проходим командой по каждой серии
          return Promise.all(
            diagramConfig.timeline.series.map(series => {
              const startDate = addToDate(series.dateRange.from)
              const endDate = addToDate(series.dateRange.till)

              return executeColumnCommand(column, startDate, endDate, series.id)
            })
          )
        } else {
          return executeColumnCommand(column)
        }
      })
      Promise.all(dataPromises)
        .then(commandDataArray => {
          // Формируем dataSet для диаграмм, проходим по каждой серии, получаем данные из колонок
          const dataSet = diagramConfig.timeline.series
            .toSorted((a, b) => a.order - b.order)
            .map((series, seriesIndex) => {
              const dataSetObject = {
                seriesTitle: series.title,
                formattedDate: getSeriesLabel(series.dateRange.from, series.dateRange.till),
              }
              diagramConfig.columns.forEach(column => {
                let diagramData
                if (column.commandForEverySeries) {
                  // Т.к. в промисе был еще один Promise.all для всех команд по сериям, то данные нужно найти по айди серии
                  diagramData = commandDataArray
                    .flat()
                    .filter(commandData => commandData.columnId === column.id)
                    .find(commandData => commandData.seriesId === series.id)?.data
                } else {
                  diagramData = commandDataArray.find(
                    commandData => commandData.columnId === column.id
                  )?.data
                }

                // Формируем данные для колонок
                if (column.jsForAllFields || column.commandForEverySeries) {
                  const funcGetData = new Function(
                    `const srcObj = {
                    data:${JSON.stringify(diagramData)},
                    series:${JSON.stringify(series)},
                    index:${seriesIndex}
                  };` + column.jsCreateValue
                  )
                  try {
                    dataSetObject[column.id] = funcGetData()
                  } catch (e) {
                    console.error(e.message)
                  }
                } else {
                  const columnField = column.fields.find(field => field.seriesId === series.id)
                  if (columnField) {
                    dataSetObject[column.id] = getFieldData(columnField, diagramData)
                  }
                }
              })

              return dataSetObject
            })
          setDiagramDataSet(dataSet)
        })
        .catch(e => {
          console.error(e.message)
        })
        .finally(() => setIsLoading(false))
    }
  }, [
    diagramConfig,
    executeCommand,
    watchFromDate,
    watchDepartment,
    watchQual,
    watchPosition,
    watchGroup,
  ])

  const getSeriesLabel = (from: number, till: number) => {
    switch (diagramConfig?.timeline.timeIncrement) {
      case INCREMENT_TYPE.HOUR:
        return `${moment(watchFromDate)
          .add(from, 'hour')
          .format(diagramConfig?.timeline.formatDate)} - ${moment(watchFromDate)
          .add(till, 'hour')
          .format(diagramConfig?.timeline.formatDate)}`
      case INCREMENT_TYPE.DAY:
        return `${moment(watchFromDate)
          .add(from, 'day')
          .format(diagramConfig?.timeline.formatDate)} - ${moment(watchFromDate)
          .add(till, 'day')
          .format(diagramConfig?.timeline.formatDate)}`
      case INCREMENT_TYPE.WEEK:
        return `${moment(watchFromDate)
          .add(from, 'week')
          .format(diagramConfig?.timeline.formatDate)} - ${moment(watchFromDate)
          .add(till, 'week')
          .format(diagramConfig?.timeline.formatDate)}`
      case INCREMENT_TYPE.MONTH:
        return `${moment(watchFromDate)
          .add(from, 'month')
          .format(diagramConfig?.timeline.formatDate)} - ${moment(watchFromDate)
          .add(till, 'month')
          .format(diagramConfig?.timeline.formatDate)}`
    }
  }

  const addToDate = (value: number) => {
    let unit: moment.unitOfTime.DurationConstructor
    switch (diagramConfig?.timeline.timeIncrement) {
      default:
      case INCREMENT_TYPE.HOUR:
        unit = 'hours'
        break
      case INCREMENT_TYPE.MINUTE:
        unit = 'minutes'
        break
      case INCREMENT_TYPE.DAY:
        unit = 'days'
        break
      case INCREMENT_TYPE.WEEK:
        unit = 'weeks'
        break
      case INCREMENT_TYPE.MONTH:
        unit = 'months'
        break
    }

    return moment(watchFromDate).add(value, unit).toISOString()
  }

  const executeColumnCommand = (column, startDate?: string, endDate?: string, seriesId?: number) =>
    new Promise((resolve, reject) => {
      const parameters = getCommandRequestData(column.command.parameters, {
        currentDate: moment(watchFromDate).toISOString(),
        column,
        startDate,
        endDate,
      })

      const filter = parameters.filter ? [parameters.filter] : []

      //TODO УБРАТЬ ХАРДКОД
      if (column.objectCode === 'roster-duty') {
        if (watchDepartment) {
          filter.push(`(staff_ref.dep_ref.dep_id=="${watchDepartment.id}")`)
        }
        if (watchQual && watchQual.id !== 'ALL') {
          filter.push(`(staff_ref.person_qual_ref=in="${watchQual.id}")`)
        }
        if (watchPosition) {
          filter.push(`(staff_ref.pos_ref.pos_id=="${watchPosition.id}")`)
        }
        if (watchGroup) {
          filter.push(`(staff_ref.group_import.group_id=="${watchGroup.id}")`)
        }
        // TODO Убрать хардкод в рамках https://jiradc.utg.group/browse/WF-7663
      } else if (
        column.objectCode === 'roster-staff-plan' ||
        column.objectCode === 'roster-staff-plan-new'
      ) {
        if (watchDepartment) {
          filter.push(`(dep_ref.dep_id=="${watchDepartment.id}")`)
        }
        if (watchQual) {
          filter.push(`(qual_ref.qual_id=="${watchQual.id}")`)
        }
      }

      const params = {
        ...parameters,
        filter: filter.join(';'),
      }
      let body
      if (column.command.httpMethod !== 'GET') {
        body = getCommandRequestData(column.command.body, {
          currentDate: moment(watchFromDate).toISOString(),
          column,
          startDate,
          endDate,
        })
      }
      executeCommand({
        commandCode: column.command.name,
        method: column.command.httpMethod,
        objectCode: column.objectCode,
        params,
        body,
      })
        .then(resp => {
          if (resp.data) {
            if (resp.data.length) {
              resolve({
                columnId: column.id,
                data: resp.data[0],
                seriesId,
              })
            } else {
              resolve({
                columnId: column.id,
                data: {},
                seriesId,
              })
            }
          } else {
            reject(resp.error || undefined)
          }
        })
        .catch(reject)
    })

  const getFieldData = (field: ColumnFieldRequestType['data'], diagramData: any) => {
    switch (field.bindType) {
      case DIAGRAM_FIELD_BIND_TYPE.FIELD:
        return diagramData[field.value]
      case DIAGRAM_FIELD_BIND_TYPE.JS: {
        const funcGetData = new Function(
          'const srcObj = ' + JSON.stringify(diagramData) + ';' + field.value
        )

        let data

        try {
          data = funcGetData()
        } catch (e) {
          console.error(e.message)
        }

        return data
      }
    }
  }

  useEffect(() => {
    if (id) {
      fetchBarChartById({ id })
        .unwrap()
        .then(resp => {
          if (resp) {
            setDiagramConfig(resp)
            if (resp.timeline.dateRange?.from) {
              setValue('fromDay', resp.timeline.dateRange?.from)
            }

            return
          }

          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    }
  }, [id])

  const socketDestinations = useMemo(() => {
    if (!diagramConfig?.columns?.length) {
      return []
    }

    return uniq(
      diagramConfig.columns
        .map(column => column.objectCode)
        .filter((objectCode): objectCode is string => Boolean(objectCode))
        .map(getObjectCodeUrl)
    )
  }, [diagramConfig])

  const handleSocketMessage = useCallback(
    (_message: IMessage) => {
      if (!diagramConfig || !watchFromDate) {
        return
      }

      configBarChartDataSet()
    },
    [diagramConfig, watchFromDate, configBarChartDataSet]
  )

  const [setIsSocketSubscribed] = useSubscription(socketDestinations, handleSocketMessage)

  useEffect(() => {
    const shouldSubscribe = socketDestinations.length > 0 && Boolean(diagramConfig)

    setIsSocketSubscribed(shouldSubscribe)

    return () => {
      setIsSocketSubscribed(false)
    }
  }, [socketDestinations, diagramConfig, setIsSocketSubscribed])

  const handlers = useHandlers({ setSelectedBar, diagramConfig })
  const inputs = useInputs({
    hideHardCodeFilters: diagramConfig ? diagramConfig.timeline.hideFilters : true,
  })

  return {
    state: {
      isLoading,
      diagramConfig,
      columns,
      diagramDataSet,
      selectedBar,
      watchFromDate,
      watchQual,
      watchDepartment,
      watchShowFormatDate,
      watchPosition,
      watchGroup,
    },
    handlers: {
      ...handlers,
    },
    inputs,
    methods,
  }
}

export default useBarDiagram
